import React, { useState, useEffect, useMemo, useCallback } from "react";
import { SingleButton } from "../../../../assets/components/ButtonGroup";
import {
  SelectCard,
  SelectCardWithSub,
} from "../../../../assets/components/SelectGroup";
import AgeSelectSheet from "../../../Ads/components/BottomSheet/AgeSelectSheet";
import ChanneSelectSheet from "../../../Ads/components/BottomSheet/ChannelSelectSheet";
import EventTitleSheet from "../../../Ads/components/BottomSheet/EventTitleSheet";
import CameraStyleSelectSheet from "../../../Ads/components/BottomSheet/CameraStyleSelectSheet"
import { HeaderIconLActionR } from "../../../../assets/components/HeaderGroup";
import { Chip } from "../../../../assets/components/Chip";
import arrowleft from "../../../../assets/icons/Arrow/left.svg";
import { useToast } from "../../../../hooks/useToast";

const ageMap = { "1": "10대", "2": "20대", "3": "30대", "4": "40대", "5": "50대", "6": "60대 이상" };
const channelMap = { "1": "카카오톡", "2": "인스타 스토리", "3": "인스타 게시물", "6": "네이버 밴드" };
// const themeMap = {"3": "이벤트" };
const styleMap = {
  "7": "원본 사용",
  "8": "배경 제거",
  "9": "배경 AI 변경",
  "10": "이미지 필터",
};

export default function Step3({
  data = {},
  updateData,
  onBack,
  onSubmit,
  image,
}) {
  const {
    age = "",
    channel = "",
    theme = "",
    customText = "",
    style = "",
    // bg_prompt = "",
    // filter = "" 
  } = data;
  const ex = data;

  const [activeSheet, setActiveSheet] = useState(null);
  const [initializedByAI, setInitializedByAI] = useState(false); // AI 기본 적용
  const aiAge = ageMap[localStorage.getItem("aiAge")] || "대상고객";
  const aiTitle = "이벤트";
  const aiChannel = channelMap[localStorage.getItem("aiChannel")] || "채널 선택";
  const imagePreview = image?.imagePreview;
  const { showToast } = useToast();

  const openSheet = (type) => setActiveSheet(type);
  const closeSheet = () => setActiveSheet(null);

  // placeholder(대상고객, 채널 선택)는 AI 추천값으로 보지 않기
  const aiAgeValue = aiAge === "대상고객" ? "" : aiAge;
  const aiChannelValue = aiChannel === "채널 선택" ? "" : aiChannel;

  // "비어있는 것만" AI로 채우는 patch
  const aiAutoPatch = useMemo(() => {
    const patch = {};

    // 1) 연령: 비어있을 때만
    if (!age && aiAgeValue) {
      patch.age = aiAgeValue;
    }

    // 2) 채널: 비어있을 때만
    if (!channel && aiChannelValue) {
      patch.channel = aiChannelValue;
    }

    // 3) 주제(이벤트): 비어있을 때만
    if (!theme) {
      patch.theme = "이벤트";
    }

    // 4) 스타일: 비어있을 때만 ("원본 사용"이 기본)
    if (!style) {
      patch.style = styleMap["7"];
      patch.bg_prompt = "";
      patch.filter = "";
    }

    return patch;
  }, [age, channel, theme, style, aiAgeValue, aiChannelValue]);

  // "이미 선택됨" = 이번에 채울 게 없음
  const isAiApplied = Object.keys(aiAutoPatch).length === 0;

  const setField = (field, value) => {
    updateData && updateData({ [field]: value });
  };

   // ✅ 초기화 가능 여부 (값이 하나라도 있으면 true)
  const canReset =
    !!age ||
    !!channel ||
    !!style ||
    !!customText ||
    (theme && theme !== "이벤트"); // "이벤트"는 기본값 취급

  const handleReset = () => {
    updateData &&
      updateData({
        age: "",
        channel: "",
        theme: "이벤트",
        customText: "",
        style: "",
        bg_prompt: "",
        filter: "",
      });
    setActiveSheet(null);
    setInitializedByAI(true); // 초기화 후에는 AI 자동 적용 막기

    showToast("초기화되었습니다", {
      placement: "bottom",
      bottomOffset: "88px",
    });
  };

  const canGoNext = !!(age && channel && style);

  const handleNext = () => {
    if (!canGoNext) return;
    onSubmit && onSubmit(); // Gallery에서 navigate 처리
  };


  const handleAIAutoSelect = useCallback(() => {
    if (Object.keys(aiAutoPatch).length === 0) return;
    updateData && updateData(aiAutoPatch);
  }, [aiAutoPatch, updateData]);
  
  // 🔹 처음 진입 시, 유저 선택이 없고 AI 값이 있으면 자동 적용
  useEffect(() => {
    if (initializedByAI) return;

    const hasUserSelection = !!age || !!channel || !!style;
    const hasAI = !!aiAgeValue || !!aiChannelValue;

    if (!hasUserSelection && hasAI && !isAiApplied) {
      handleAIAutoSelect();
      setInitializedByAI(true);
    }
  }, [initializedByAI, age, channel, style, aiAgeValue, aiChannelValue, isAiApplied, handleAIAutoSelect]);




  return (
    <div className="relative flex h-dvh min-h-dvh flex-col overflow-hidden bg-surface-default">
      <HeaderIconLActionR
        title=""
        actionText="초기화"
        onAction={canReset ? handleReset : undefined}
        icon={arrowleft}
        onIconClick={onBack}
      />

      <div className="min-h-0 flex-1 overflow-y-auto p-[16px] [touch-action:pan-y] [-webkit-overflow-scrolling:touch]">
        <div className="flex items-center justify-between pb-[32px]">
          <h2 className="text-t2">옵션 설정</h2>
          <Chip
            chip="d"
            label="AI 자동 선택"
            // status="default"
            status={"selected"}
            onClick={() => {
              if (isAiApplied) {
                showToast("이미 선택되어 있습니다", {
                  placement: "bottom",
                  bottomOffset: "88px",
                });
              } else {
                handleAIAutoSelect();
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-[16px]">
          <SelectCard
            label="홍보 고객"
            value={age}
            placeholder="고객 선택"
            onClick={() => openSheet("age")}
          />

          <SelectCard
            label="홍보 채널"
            value={channel}
            placeholder="채널 선택"
            onClick={() => openSheet("channel")}
          />

          <SelectCardWithSub
            label="이벤트 내용"
            subLabel={customText || ""}
            subPlaceholder="작성한 내용이 없습니다"
            // value={theme}
            value={theme === "이벤트" ? "" : theme || ""}
            placeholder=" "
            onClick={() => openSheet("theme")}
          />

          <SelectCard
            label="이미지 스타일"
            value={style}
            placeholder="스타일 선택"
            onClick={() => openSheet("style")}
          />
        </div>
      </div>

      <div
        className="shrink-0 border-t border-border-subtle bg-surface-default/95 backdrop-blur-[8px]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <SingleButton
          type="fill"
          disabled={!canGoNext}
          onClick={handleNext}
        >
          이미지 생성하기
        </SingleButton>
      </div>

      {/* 고객 */}
      <AgeSelectSheet
        isOpen={activeSheet === "age"}
        value={age}
        aiAge={aiAge}
        onClose={closeSheet}
        onConfirm={(value) => {
          setField("age", value)
        }}
      />


      {/* 채널 */}
      <ChanneSelectSheet
        isOpen={activeSheet === "channel"}
        onClose={closeSheet}
        value={channel}
        onSelect={(opt) => setField("channel", opt)}
      />

      {/* 주제 */}
      <EventTitleSheet
        isOpen={activeSheet === "theme"}
        onClose={closeSheet}
        value={theme}
        aiTitle={aiTitle}
        detailValue={customText}
        data={data}
        exData={ex}
        updateData={updateData}
        onConfirm={({ theme, customText }) => {
          setField("theme", theme);
          setField("customText", customText);
        }}
      />


      {/* 스타일 */}
      <CameraStyleSelectSheet
        isOpen={activeSheet === "style"}
        onClose={closeSheet}
        value={style}
        aiStyle={"원본 사용"}
        onConfirm={(value, bg_prompt, filter) => {
          setField("style", value);
          setField("bg_prompt", bg_prompt);
          setField("filter", filter);
        }}
        image={imagePreview}
      />
    </div>
  );
}
